import {FC, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {api, EOAuthType, hasValue, useT} from '@kcast-fe/core';
import {Box, Button, Flex} from '@chakra-ui/react';
import {ICredentialsForm, ITotpForm} from 'core';
import {AxiosError} from 'axios';

import {BaseFieldsForm, VerifyFieldsForm, TotpFieldsForm} from './components';

interface IProps {
  service: EOAuthType;
  onClose: () => void;
  onIsVerification: (value: boolean) => void;
  onIsTwoFactor: (value: boolean) => void;
}

export const CredentialsForm: FC<IProps> = ({
  service,
  onIsVerification,
  onIsTwoFactor,
  onClose
}) => {
  const connectMutation = api.oauth.useConnectMutation();

  const [requestError, setRequestError] = useState('');
  const [verifyUrl, setVerifyUrl] = useState('');
  const [sessionToken, setSessionToken] = useState('');

  const {t} = useT();

  const {control, handleSubmit, formState, getValues} = useForm<ICredentialsForm>({
    defaultValues: {username: '', password: '', hv_token: '', hv_token_type: 'captcha'}
  });

  const {
    control: totpControl,
    handleSubmit: handleTotpSubmit,
    formState: totpFormState
  } = useForm<ITotpForm>({
    defaultValues: {totp_code: ''}
  });

  useEffect(() => {
    onIsVerification(hasValue(verifyUrl));
  }, [onIsVerification, verifyUrl]);

  useEffect(() => {
    onIsTwoFactor(hasValue(sessionToken));
  }, [onIsTwoFactor, sessionToken]);

  const handleError = (e: Error) => {
    const error = e as AxiosError;
    if (error?.status === 449 && error.response?.data) {
      const body = error.response.data as {
        detail?: {web_url?: string; error?: string; session_token?: string};
      };
      if (body.detail?.error === 'two_factor_required' && body.detail?.session_token) {
        setSessionToken(body.detail.session_token);
      } else if (body.detail?.web_url && body.detail?.error) {
        setVerifyUrl(body.detail.web_url);
      }
    } else if (error.response?.data) {
      const body = error.response.data as {detail?: string};
      setRequestError(body.detail ?? 'Unknown server error');
    } else {
      setRequestError(e.message ?? 'Unknown server error');
    }
  };

  const submitConnect = (data: ICredentialsForm) => {
    setVerifyUrl('');
    connectMutation.mutate(
      {...data, oauthType: service},
      {
        onSuccess: () => onClose(),
        onError: handleError
      }
    );
  };

  const submitTotp = (data: ITotpForm) => {
    const credentials = getValues();
    connectMutation.mutate(
      {
        oauthType: service,
        username: credentials.username,
        password: credentials.password,
        session_token: sessionToken,
        totp_code: data.totp_code
      },
      {
        onSuccess: () => onClose(),
        onError: handleError
      }
    );
  };

  const isTwoFactor = hasValue(sessionToken);
  const isVerify = !isTwoFactor && hasValue(verifyUrl);

  const activeFormState = isTwoFactor ? totpFormState : formState;
  const activeSubmit = isTwoFactor ? handleTotpSubmit(submitTotp) : handleSubmit(submitConnect);

  return (
    <Box p={'0 24px'}>
      <Box>
        {isTwoFactor ? (
          <TotpFieldsForm control={totpControl} />
        ) : isVerify ? (
          <VerifyFieldsForm control={control} verifyUrl={verifyUrl} />
        ) : (
          <BaseFieldsForm control={control} onSetRequestError={setRequestError} />
        )}

        {hasValue(requestError) && (
          <Box
            pos={'absolute'}
            color={'stateError'}
            className={'cut-text'}
            bottom={'80px'}
            right={'30px'}
            left={'30px'}
          >
            {requestError}
          </Box>
        )}
      </Box>

      <Flex justifyContent={'flex-end'} p={'40px 4px 0 0'} justifySelf={'flex-end'}>
        <Flex gap={'12px'}>
          <Button size={'lg'} variant={'subtle'} onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button
            size={'lg'}
            variant={'solid'}
            disabled={!activeFormState.isValid || connectMutation.isPending}
            onClick={activeSubmit}
          >
            {t('actions.connect')}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
